import React, { useState, useEffect, useRef, memo } from 'react';
import { Mic, X, Loader2, Volume2, VolumeX, MicOff, Bot, Sparkles, RefreshCw } from 'lucide-react';
import { GoogleGenAI, LiveServerMessage, Modality, Type, FunctionDeclaration } from '@google/genai';
import { CourseSelectionForm } from './CourseSelectionForm';
import { CourseDisplay } from './CourseDisplay';
import { InfoDisplay } from './InfoDisplay';
import { CourseSelectionData, AdmissionData, Course } from '../types';
import { knowledgeService } from '../services/knowledgeService';
import {
  getAllCourses,
} from '../api';

// --- Utils ---

function encode(bytes: Uint8Array) {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function decode(base64: string) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function createPcmBlob(data: Float32Array): { data: string, mimeType: string } {
  const l = data.length;
  const int16 = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    int16[i] = data[i] * 32768;
  }
  return {
    data: encode(new Uint8Array(int16.buffer)),
    mimeType: 'audio/pcm;rate=16000',
  };
}

async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}

// --- Data Mapping Utils ---

function mapProgramToCourse(program: any): Course {
  let category: any = program.category;
  if (program.name.toLowerCase().includes('textile science')) category = 'Textile Science';
  else if (program.name.toLowerCase().includes('textile design')) category = 'Textile Design';
  else if (program.name.toLowerCase().includes('textile management')) category = 'Textile Management';
  else if (program.name.toLowerCase().includes('apparel')) category = 'Apparel Management';
  else if (program.name.toLowerCase().includes('fashion')) category = 'Fashion Design';
  else if (program.name.toLowerCase().includes('industrial')) category = 'Industrial Management';

  return {
    id: program.id,
    name: program.name,
    description: program.description,
    duration: program.duration,
    level: 'Undergraduate',
    category,
    benefits: program.benefits,
    fees: 12000,
    university_id: 'aziz-edu-1',
    prerequisites: [program.admissionCriteria],
    code: program.id.toUpperCase()
  };
}

// --- Tool Definitions ---

const openFormTool: FunctionDeclaration = {
  name: 'openForm',
  description: 'Opens a specific interactive form modal for the user to engage with. Use this when you detect user interest in exploring programs or applying for admission. This creates a visual interface that enhances the user experience.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      formType: {
        type: Type.STRING,
        enum: ['course-selection', 'program-list'],
        description: 'Type of form to open: course-selection for helping users apply for programs, program-list for displaying available programs'
      },
    },
    required: ['formType'],
  },
};

const closeFormTool: FunctionDeclaration = {
  name: 'closeForm',
  description: 'Close the currently open form.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const fetchProgramsTool: FunctionDeclaration = {
  name: 'fetchPrograms',
  description: 'Get list of Aziz Edu programs and display them. Can filter by category (Computer Science, Software Engineering, Artificial Intelligence, Cybersecurity, Data Science, Cloud Computing, IT, Robotics), or search query.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      category: { type: Type.STRING, description: 'Optional: Computer Science, Software Engineering, AI, Cybersecurity, Data Science, Cloud Computing, IT, Robotics' },
      query: { type: Type.STRING, description: 'Optional: Search term like "computer science", "AI", "cybersecurity", "data science", "cloud", "software", "programming"' },
    },
  },
};

const getProgramDetailsTool: FunctionDeclaration = {
  name: 'getProgramDetails',
  description: 'Get detailed information about a specific Aziz Edu program including duration, credit hours, admission criteria, and career prospects.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      programName: { type: Type.STRING, description: 'Name of the program to get details for' },
    },
    required: ['programName'],
  },
};

const getAdmissionRequirementsTool: FunctionDeclaration = {
  name: 'getAdmissionRequirements',
  description: 'Get detailed admission requirements for Aziz Edu including eligibility criteria, required documents, and application process.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const getFeeStructureTool: FunctionDeclaration = {
  name: 'getFeeStructure',
  description: 'Get detailed fee structure for Aziz Edu including admission fees, tuition fees, hostel fees, transport fees, and refund policy.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const getAdmissionScheduleTool: FunctionDeclaration = {
  name: 'getAdmissionSchedule',
  description: 'Get admission schedule for upcoming semester including application deadlines, test dates, and semester start date.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const getPlacementInfoTool: FunctionDeclaration = {
  name: 'getPlacementInfo',
  description: 'Get placement statistics and information about industry partnerships and hiring companies.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const populateFormFieldTool: FunctionDeclaration = {
  name: 'populateFormField',
  description: 'Fill a specific field in the active form.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      formType: { type: Type.STRING, enum: ['course-selection'] },
      fieldName: { type: Type.STRING, description: 'Field to update (e.g. student_name, student_phone, student_email, city, selected_course)' },
      value: { type: Type.STRING, description: 'Value to set. Convert numbers to strings.' },
    },
    required: ['formType', 'fieldName', 'value'],
  },
};

const validateFormTool: FunctionDeclaration = {
  name: 'validateForm',
  description: 'Check if the current form has all required fields filled.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      formType: { type: Type.STRING, enum: ['course-selection'] },
    },
    required: ['formType'],
  },
};

const submitApplicationTool: FunctionDeclaration = {
  name: 'submitApplication',
  description: 'RESTRICTED: Finalize and submit the application. Only use when user explicitly confirms.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const terminateSessionTool: FunctionDeclaration = {
  name: 'terminateSession',
  description: 'Gracefully end the conversation and close the voice bot interface. Call this after the user says goodbye or when the interaction is complete.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const getContactInfoTool: FunctionDeclaration = {
  name: 'getContactInfo',
  description: 'Get contact information for Aziz Edu University including address, phone number, and office hours.',
  parameters: { type: Type.OBJECT, properties: {} },
};

const SYSTEM_INSTRUCTION = `
You are the official AI Voice Admission Counselor for Aziz Edu University of Technology.
Aziz Edu is a leading computer science and technology university in Karachi, Pakistan, established in 2010.

**Programs Offered:**
- BS Computer Science (CS)
- BS Software Engineering (SE)
- BS Artificial Intelligence (AI)
- BS Cybersecurity
- BS Data Science
- BS Cloud Computing
- BS Information Technology (IT)
- BS Robotics & Automation

### 🌍 LANGUAGE POLICY (CRITICAL)
**ALWAYS match the user's language exactly:**
- If user speaks **English** → Respond in English
- If user speaks **Urdu** (اردو) → Respond in Pakistani Urdu
- If user speaks **any other language** → Respond in that same language
- If user mixes languages (e.g., Urdu-English code-switching) → Match their mix naturally
- **NEVER** force English if the user initiated in another language
- **NEVER** switch languages unless the user does first

**Tone:** Professional, warm, and tech-savvy. Never guarantee admission/merit.

### 1. VISUAL & VOICE SYNC (CRITICAL)
Your UI is your strongest tool. Users MUST see details on screen while you speak about them.
- **Show Catalog**: Call 'fetchPrograms' immediately if they ask about offerings.
- **Show Details**: Call 'getProgramDetails' when discussing a specific program (shows coordinator, benefits, career prospects).
- **Show Fees**: Call 'getFeeStructure' immediately if they ask about tuition, hostel, or refunds.
- **Show Requirements**: Call 'getAdmissionRequirements' for eligibility criteria.
- **Show Deadlines**: Call 'getAdmissionSchedule' for admission dates.
- **Show Success**: Call 'getPlacementInfo' for career outcomes and hiring companies.
- **Show Application Form**: Call 'openForm("course-selection")' when user shows interest in applying or admission. This opens a visual form for the user to fill out.

### 2. CONVERSATION FLOW
- **Phase 1: Discovery**: Greet in the user's language ("Salaam" for Pakistani style Urdu, "Hello" for English, etc.). Show offerings via 'fetchPrograms'.
- **Phase 2: Deep Dive**: Use the tools above to SYNC your voice with on-screen data panels. Continue in user's language.
- **Phase 3: Application Intent**: When user shows interest in applying, call 'openForm("course-selection")' IMMEDIATELY to show the admission inquiry form.
- **Phase 4: Lead Capture**: After form is open, guide field-by-field. Call 'populateFormField' for EACH field (student_name, student_phone, student_email, city, selected_course). Clarify that this is an inquiry/callback request, not the final admission form.
- **Phase 5: Commitment**: Call 'validateForm' → Confirm → 'submitApplication'. Remind them that a real Aziz Edu representative will call them soon after submission.
- **Phase 6: Exit**: Final goodbye in user's language → Call 'terminateSession'.

### 3. KEY INFORMATION
- **Admission Requirements**: Minimum 45-55% in Intermediate (varies by program), Mathematics required for most CS programs
- **Fee Structure**: Rs. 12,000 per credit hour, ~Rs. 200,000 per semester
- **Location**: ST-2/A, Sector 15, Korangi Industrial Area, Karachi
- **Contact**: (92-21) 3500-1234, info@azizedu.edu.pk
- **Placement**: 95% placement rate, graduates at Google, Microsoft, Systems Limited, Careem

### 4. OPERATIONAL RULES
- IMPORTANT: Explicitly mention that an Aziz Edu representative will contact the student for further processing.
- BARGE-IN: Stop talking immediately if interrupted.
- NO GUESSING: Use tools for all Aziz Edu-specific data.
- ESCALATE: For complaints or errors, provide contact info via 'getContactInfo'.
- LANGUAGE: Always detect and mirror the user's language choice throughout the conversation.
`;

const VoiceChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeForm, setActiveForm] = useState<'none' | 'course-selection' | 'program-list' | 'fee-structure' | 'admission-requirements' | 'admission-schedule' | 'placement-info' | 'contact-info' | 'program-details'>('none');
  const [infoData, setInfoData] = useState<any>(null);
  const [visualCourses, setVisualCourses] = useState<Course[]>(knowledgeService.getPrograms().map(mapProgramToCourse));
  const [status, setStatus] = useState<'idle' | 'connecting' | 'listening' | 'speaking' | 'processing' | 'error' | 'denied'>('idle');
  const [isAiMuted, setIsAiMuted] = useState(false);

  // Data States
  const [courses, setCourses] = useState<Course[]>(knowledgeService.getPrograms().map(mapProgramToCourse));
  const [selectionData, setSelectionData] = useState<AdmissionData>({
    student_name: '',
    student_email: '',
    student_phone: '',
    selected_course: knowledgeService.getPrograms()[0]?.name || '',
    city: ''
  });

  // Refs
  const selectionDataRef = useRef(selectionData);
  const coursesRef = useRef(courses);

  useEffect(() => {
    selectionDataRef.current = selectionData;
    coursesRef.current = courses;
  }, [selectionData, courses]);

  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const outputAudioContextRef = useRef<AudioContext | null>(null);
  const nextStartTimeRef = useRef<number>(0);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sessionRef = useRef<any>(null);

  const API_KEY = import.meta.env.VITE_GEMINI_API_KEY;

  if (!API_KEY) {
    throw new Error("VITE_GEMINI_API_KEY is missing");
  }


  useEffect(() => {
    const tipCourses = knowledgeService.getPrograms().map(mapProgramToCourse);
    setCourses(tipCourses);
    setVisualCourses(tipCourses);
  }, []);

  const cleanup = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (processorRef.current) processorRef.current.disconnect();
    if (inputAudioContextRef.current?.state !== 'closed') inputAudioContextRef.current?.close();
    if (outputAudioContextRef.current?.state !== 'closed') outputAudioContextRef.current?.close();
    sourcesRef.current.forEach(s => { try { s.stop(); } catch { } });
    sourcesRef.current.clear();
    nextStartTimeRef.current = 0;
  };

  const stopAudioPlayback = () => {
    sourcesRef.current.forEach(source => {
      try { source.stop(); } catch (e) { /* ignore already stopped */ }
    });
    sourcesRef.current.clear();
    // Reset the scheduler pointer to current time to avoid large delays when resuming
    if (outputAudioContextRef.current) {
      nextStartTimeRef.current = outputAudioContextRef.current.currentTime;
    }
  };

const startSession = async () => {
    cleanup();
    setStatus('connecting');

    try {
      // Initialize Audio Contexts early
      inputAudioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
      outputAudioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });

      const outputNode = outputAudioContextRef.current.createGain();
      outputNode.connect(outputAudioContextRef.current.destination);

      // Start stream request in parallel with AI connection
      const streamPromise = navigator.mediaDevices.getUserMedia({ audio: true });

      const ai = new GoogleGenAI({ apiKey: API_KEY });

      const sessionPromise = ai.live.connect({
        model: 'gemini-2.5-flash-native-audio-preview-12-2025',
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Zephyr' } } },
          systemInstruction: SYSTEM_INSTRUCTION,
          inputAudioTranscription: {},
          outputAudioTranscription: {},
          tools: [{
            functionDeclarations: [
              openFormTool, closeFormTool, fetchProgramsTool, getProgramDetailsTool,
              getAdmissionRequirementsTool, getFeeStructureTool, getAdmissionScheduleTool,
              getPlacementInfoTool, populateFormFieldTool, validateFormTool,
              submitApplicationTool, getContactInfoTool, terminateSessionTool
            ]
          }],
        },
        callbacks: {
          onopen: async () => {
            try {
              // Wait for stream to be ready if it isn't already
              const stream = await streamPromise;
              streamRef.current = stream;

              setStatus('listening');

              if (!inputAudioContextRef.current) return;
              const source = inputAudioContextRef.current.createMediaStreamSource(stream);
              const scriptProcessor = inputAudioContextRef.current.createScriptProcessor(4096, 1, 1);
              processorRef.current = scriptProcessor;

              scriptProcessor.onaudioprocess = (e) => {
                const inputData = e.inputBuffer.getChannelData(0);
                const pcmBlob = createPcmBlob(inputData);
                sessionPromise.then(session => session.sendRealtimeInput({ media: pcmBlob }));
              };
              source.connect(scriptProcessor);
              scriptProcessor.connect(inputAudioContextRef.current.destination);
            } catch (err) {
              console.error("Stream initialization error:", err);
              setStatus('error');
              cleanup();
            }
          },
          onmessage: async (message: LiveServerMessage) => {
            // Handle User Interruption (Barge-in)
            if (message.serverContent?.interrupted) {
              console.log('User interrupted - stopping audio');
              stopAudioPlayback();
              setStatus('listening');
              return;
            }

            if (message.serverContent?.turnComplete) {
              setStatus('listening');
            }

            if (message.toolCall) {
              setStatus('processing');
              const responses = [];

              for (const fc of message.toolCall.functionCalls) {
                let result: any = { status: 'success' };
                const args = fc.args as any;

                try {
                  // --- Tool Handlers --- (keep your existing handlers unchanged)
                  if (fc.name === 'openForm') {
                    setActiveForm(args.formType);
                    if (args.formType === 'program-list') {
                      setVisualCourses(knowledgeService.getPrograms().map(mapProgramToCourse));
                    }
                    result = { status: 'opened', form: args.formType };
                  }

                  else if (fc.name === 'closeForm') {
                    setActiveForm('none');
                    result = { status: 'closed' };
                  }

                  else if (fc.name === 'fetchPrograms') {
                    const { category, query } = args;
                    let programs = knowledgeService.getPrograms();
                    if (category) programs = knowledgeService.getProgramsByCategory(category);
                    if (query) programs = knowledgeService.searchPrograms(query);
                    const convertedCourses = programs.map(mapProgramToCourse);
                    setVisualCourses(convertedCourses);
                    setActiveForm('program-list');
                    const programsSummary = programs.map(program => ({
                      name: program.name,
                      category: program.category,
                      duration: program.duration,
                      creditHours: program.creditHours,
                      description: program.description
                    }));
                    result = { programs: programsSummary, uiAction: "displayed_to_user" };
                  }

                  else if (fc.name === 'getProgramDetails') {
                    const { programName } = args;
                    const program = knowledgeService.getProgramByName(programName);
                    if (program) {
                      setInfoData(program);
                      setActiveForm('program-details');
                      result = { programDetails: program, uiAction: "displayed_to_user" };
                    } else {
                      result = { status: 'error', message: `Could not find details for program: ${programName}` };
                    }
                  }

                  else if (fc.name === 'getAdmissionRequirements') {
                    const admissionProcess = knowledgeService.getAdmissionProcess();
                    const data = {
                      eligibility: [
                        'Minimum 45% marks in Intermediate with all subjects passed',
                        'Pre-Engineering background is mandatory for BS Textile Science',
                        'A-Level candidates must have Mathematics, Chemistry, and Physics with minimum equivalent of 45%'
                      ],
                      requiredDocuments: admissionProcess.requiredDocuments,
                      processSteps: admissionProcess.steps,
                      selectionProcess: admissionProcess.selectionProcess
                    };
                    setInfoData(data);
                    setActiveForm('admission-requirements');
                    result = { admissionRequirements: data, uiAction: "displayed_to_user" };
                  }

                  else if (fc.name === 'getFeeStructure') {
                    const feeStructure = knowledgeService.getFeeStructure();
                    setInfoData(feeStructure);
                    setActiveForm('fee-structure');
                    result = { feeStructure: feeStructure, uiAction: "displayed_to_user" };
                  }

                  else if (fc.name === 'getAdmissionSchedule') {
                    const admissionCalendar = knowledgeService.getAdmissionCalendar();
                    const data = {
                      fall2026: {
                        admissionOpening: admissionCalendar.admissionOpening,
                        lastDateToApply: admissionCalendar.lastDateToApply,
                        entryTest: admissionCalendar.entryTestDates,
                        interview: admissionCalendar.interviewSchedule,
                        meritList: admissionCalendar.meritListDates,
                        feeSubmissionDeadline: admissionCalendar.feeDeadline,
                        semesterStartDate: admissionCalendar.semesterStartDate
                      }
                    };
                    setInfoData(data);
                    setActiveForm('admission-schedule');
                    result = { admissionSchedule: data, uiAction: "displayed_to_user" };
                  }

                  else if (fc.name === 'getPlacementInfo') {
                    const placementData = knowledgeService.getPlacementData();
                    const data = {
                      placementRatio: placementData.placementRatio,
                      topHiringPartners: placementData.topHiringCompanies,
                      internshipPartners: placementData.internshipPartners,
                      successStory: placementData.successStories[0],
                      industryMou: placementData.industryMous[0]
                    };
                    setInfoData(data);
                    setActiveForm('placement-info');
                    result = { placementInfo: data, uiAction: "displayed_to_user" };
                  }

                  else if (fc.name === 'getContactInfo') {
                    const institution = knowledgeService.getInstitutionInfo();
                    const data = {
                      universityName: institution.fullOfficialName,
                      recognition: institution.recognition,
                      charter: institution.charter,
                      established: institution.yearOfEstablishment.toString(),
                      campusAddress: institution.campusAddress,
                      department: institution.department,
                      officeTiming: institution.officeTimings,
                      contactNumber: institution.contactInfo
                    };
                    setInfoData(data);
                    setActiveForm('contact-info');
                    result = { contactInfo: data, uiAction: "displayed_to_user" };
                  }

                  else if (fc.name === 'populateFormField') {
                    const { formType, fieldName, value } = args;
                    if (formType === 'course-selection') {
                      setSelectionData(prev => ({ ...prev, [fieldName]: value }));
                    }
                    result = { status: 'updated', field: fieldName, value };
                  }

                  else if (fc.name === 'validateForm') {
                    const missing = [];
                    if (args.formType === 'course-selection') {
                      if (!selectionDataRef.current.student_name) missing.push('student_name');
                      if (!selectionDataRef.current.student_phone) missing.push('student_phone');
                      if (!selectionDataRef.current.selected_course) missing.push('selected_course');
                      if (!selectionDataRef.current.city) missing.push('city');
                    }
                    result = { isValid: missing.length === 0, missingFields: missing };
                  }

                  else if (fc.name === 'terminateSession') {
                    setTimeout(() => {
                      setIsOpen(false);
                      cleanup();
                    }, 4000);
                    result = { status: 'session_terminated' };
                  }

                  else if (fc.name === 'submitApplication') {
                    const data = selectionDataRef.current;
                    setActiveForm('none');
                    result = { success: true, applicationSubmitted: true, programSelected: data.selected_course };
                  }

                } catch (e: any) {
                  console.error(`Tool error (${fc.name}):`, e);
                  result = { error: e.message || "Operation failed" };
                }

                responses.push({
                  id: fc.id,
                  name: fc.name,
                  response: { result }
                });
              }

              sessionPromise.then(session => session.sendToolResponse({ functionResponses: responses }));
            }

            // Audio Playback
            const base64Audio = message.serverContent?.modelTurn?.parts?.[0]?.inlineData?.data;
            if (base64Audio && outputAudioContextRef.current) {
              setStatus('speaking');
              const ctx = outputAudioContextRef.current;
              nextStartTimeRef.current = Math.max(nextStartTimeRef.current, ctx.currentTime);
              const buffer = await decodeAudioData(decode(base64Audio), ctx, 24000, 1);
              const source = ctx.createBufferSource();
              source.buffer = buffer;
              source.connect(outputNode);
              source.start(nextStartTimeRef.current);
              nextStartTimeRef.current += buffer.duration;
              sourcesRef.current.add(source);
              source.onended = () => {
                sourcesRef.current.delete(source);
                if (sourcesRef.current.size === 0) setStatus('listening');
              };
            }
          },
          onerror: (e) => { console.error(e); setStatus('error'); cleanup(); },
          onclose: () => { setStatus('idle'); cleanup(); }
        }
      });
      sessionRef.current = sessionPromise;

      // Handle mic permission errors
      streamPromise.catch(e => {
        console.error("Mic access denied or failed", e);
        setStatus(e.name === 'NotAllowedError' ? 'denied' : 'error');
        sessionPromise.then(s => s.close && s.close()).catch(() => { });
        cleanup();
      });

    } catch (err: any) {
      console.error(err);
      setStatus(err.name === 'NotAllowedError' ? 'denied' : 'error');
      cleanup();
    }
  };

  const handleToggle = () => {
    if (isOpen) {
      if (sessionRef.current) sessionRef.current.then((s: any) => s.close && s.close());
      cleanup();
      setIsOpen(false);
      setActiveForm('none');
      setStatus('idle');
    } else {
      setIsOpen(true);
      startSession();
    }
  };

  const closeForm = () => setActiveForm('none');

  const handleManualSubmit = (data: { type: string, data: any, result: any }) => {
    closeForm();

    // Inject system event into AI session to synchronize state
    if (sessionRef.current) {
      sessionRef.current.then((session: any) => {
        let text = "";
        if (data.type === 'course-selection') {
          text = `[System Event] User has manually successfully completed the admission application. Selected program: ${data.data.selected_course}. Please acknowledge this to the user immediately.`;
        }

        if (text) {
          // Send text as user input to update context
          session.send({
            client_content: {
              turns: [{
                role: 'user',
                parts: [{ text }]
              }],
              turn_complete: true
            }
          });
        }
      });
    }
  };

  const toggleAiMute = () => {
    if (outputAudioContextRef.current) {
      if (isAiMuted) {
        outputAudioContextRef.current.resume();
      } else {
        outputAudioContextRef.current.suspend();
      }
      setIsAiMuted(!isAiMuted);
    }
  };

  const resetSession = () => {
    if (sessionRef.current) {
      sessionRef.current.then((s: any) => s.close && s.close());
    }
    startSession();
  };

  return (
    <>
      {!isOpen && (
        <>
          {/* Tooltip/Popup Label - Hidden on mobile, visible on larger screens */}
          <div className="hidden sm:block fixed bottom-32 right-6 z-40 pointer-events-none animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="bg-slate-900 text-white px-4 py-2.5 rounded-2xl shadow-2xl flex items-center gap-2 relative">
              {/* Arrow pointing to button */}
              <div className="absolute -bottom-2 right-6 w-4 h-4 bg-slate-900 rotate-45"></div> 
              
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-sm font-bold whitespace-nowrap">Need Help? Chat Now!</span>
              </div>
            </div>
          </div>
          
          {/* Floating Action Button */}
          <button
            onClick={handleToggle}
            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 group"
          >
            {/* Outer pulse ring - always visible */}
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-full animate-ping opacity-20 blur-md" />
            
            {/* Main button container */}
            <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 hover:from-slate-800 hover:via-slate-700 hover:to-slate-800 text-white px-6 py-4 sm:px-7 sm:py-5 rounded-full shadow-2xl transition-all duration-300 flex items-center gap-3 font-bold hover:shadow-slate-900/50 hover:shadow-2xl hover:scale-110 active:scale-95 overflow-hidden border-2 border-white/20">
              
              {/* Shimmer effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              
              {/* Rotating border effect */}
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-white/10 group-hover:border-white/30 transition-colors duration-500 animate-spin-slow" />
              
              {/* Icon with background */}
              <div className="relative">
                {/* Glow effect behind icon */}
                <div className="absolute inset-0 bg-cyan-500/30 rounded-full blur-md group-hover:bg-cyan-400/50 transition-colors" />
                
                {/* Icon container */}
                <div className="relative bg-gradient-to-br from-cyan-500 to-blue-600 p-2.5 sm:p-3 rounded-full group-hover:rotate-12 transition-transform duration-300 shadow-lg">
                  <Bot size={22} className="sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              
              {/* Text with gradient */}
              <div className="flex flex-col items-start">
                <span className="text-xs sm:text-sm font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">AI Advisor</span>
                <span className="text-[9px] sm:text-[10px] text-gray-400 font-medium">24/7 Support</span>
              </div>
              
              {/* Online status indicator */}
              <div className="absolute -top-1 -right-1">
                <div className="relative">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 bg-emerald-500 rounded-full border-2 border-slate-900 animate-pulse" />
                  <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-50" />
                </div>
              </div>
              
              {/* Notification dot */}
              <div className="absolute top-2 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse border border-slate-900" />
            </div>
          </button>
        </>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-[100] bg-indigo-50/10 backdrop-blur-3xl flex flex-col animate-boot isolate overflow-hidden max-w-screen">
          <div className="scan-line-overlay" />
          {/* Ambient Background Elements - Contained */}
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/10 rounded-full blur-[120px] animate-pulse pointer-events-none" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-500/10 rounded-full blur-[120px] animate-pulse-slow pointer-events-none" />

          {/* Header - Compact */}
          <div className="flex-shrink-0 p-4 lg:p-6 flex justify-between items-center z-20 animate-flicker">
            <div className="flex items-center gap-3 text-indigo-600">
              <div className="p-2 bg-white/40 backdrop-blur-xl rounded-xl shadow-premium-indigo border border-white/40">
                <Sparkles className="animate-pulse text-indigo-600" size={20} />
              </div>
              <div className="animate-glitch">
                <h2 className="font-sans font-bold text-xl md:text-2xl tracking-tight text-gray-900">Aziz Edu AI Advisor</h2>
                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-indigo-600/60 mt-0.5">Autonomous Support</p>
              </div>
            </div>
            <button
              onClick={handleToggle}
              className="p-2 rounded-xl bg-white/40 backdrop-blur-xl hover:bg-red-50 text-gray-500 hover:text-red-500 transition-all shadow-lg hover:rotate-90 active:scale-90 border border-white/40"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-grow flex flex-col md:flex-row overflow-hidden relative perspective-2000 animate-flicker" style={{ animationDelay: '0.2s' }}>

            {/* Visualizer Panel - 3D Orb Section */}
            <div className={`flex flex-col items-center justify-center transition-all duration-1000 transform-style-3d ${activeForm !== 'none' ? 'md:w-1/2' : 'w-full'}`}>

              <div className="flex flex-col items-center gap-4 p-4 md:p-6 animate-in zoom-in-95 duration-1000">

                {/* Advanced 3D Orb Component - Top Position */}
                <div className="orb-container group shadow-premium-blue rounded-full origin-center w-[120px] h-[120px] sm:w-[140px] sm:h-[140px] md:w-[160px] md:h-[160px] lg:w-[180px] lg:h-[180px] relative flex items-center justify-center">
                  {/* Outer Rings */}
                  <div className={`absolute inset-0 border-2 border-blue-400/20 rounded-full animate-ring-spin ${status === 'speaking' ? 'scale-125 border-blue-500/40' : ''}`} />
                  <div className={`absolute inset-[-10%] border-2 border-yellow-400/10 rounded-full animate-spin-reverse-slow ${status === 'listening' ? 'scale-105 border-yellow-500/30' : ''}`} style={{ borderStyle: 'dashed' }} />

                  {/* Core Orb */}
                  <div className={`relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 rounded-full flex items-center justify-center shadow-2xl transition-all duration-1000 transform-style-3d animate-float3d
                    ${status === 'speaking' ? 'shadow-glow-blue bg-gradient-to-br from-blue-600 to-indigo-700' :
                      status === 'listening' ? 'shadow-glow-yellow bg-gradient-to-br from-yellow-400 to-amber-500' :
                        status === 'processing' ? 'shadow-glow-blue bg-gradient-to-br from-blue-500 to-cyan-500' :
                          status === 'error' || status === 'denied' ? 'bg-red-600 shadow-red-500/50' :
                            'bg-gradient-to-br from-gray-100 to-white shadow-gray-300/50'
                    }`}>

                    {/* Inner Pulse Effect */}
                    <div className="absolute inset-3 rounded-full border border-white/20 animate-orb-inner" />

                    {/* Icon centered in Orb */}
                    <div className="relative z-10 text-white drop-shadow-lg">
                      {status === 'speaking' ? <Volume2 className="animate-pulse w-8 h-8 sm:w-10 sm:h-10" strokeWidth={1.5} /> :
                        status === 'listening' ? <Mic className="animate-float-gentle w-8 h-8 sm:w-10 sm:h-10" strokeWidth={1.5} /> :
                          status === 'processing' ? <Loader2 className="animate-spin w-8 h-8 sm:w-10 sm:h-10" strokeWidth={1.5} /> :
                            <Bot className="opacity-80 w-8 h-8 sm:w-10 sm:h-10" strokeWidth={1.5} />}
                    </div>
                  </div>
                </div>

                {/* Status UI - Below Orb */}
                <div className="text-center space-y-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/40 backdrop-blur-md border border-white/50 text-[8px] lg:text-[9px] font-black uppercase tracking-widest text-blue-600 shadow-sm">
                    {status === 'listening' ? <span className="flex h-2 w-2 rounded-full bg-red-500 animate-ping" /> : null}
                    {status === 'speaking' ? <span className="flex h-2 w-2 rounded-full bg-blue-500 animate-pulse" /> : null}
                    Status: {status}
                  </div>
                  <h3 className="font-sans text-xl md:text-2xl lg:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 leading-tight">
                    {status === 'listening' ? 'Tell me more...' :
                      status === 'speaking' ? "I'm explaining..." :
                        status === 'processing' ? 'Analyzing...' :
                          status === 'error' ? 'System Halt' : 'Ready to help'}
                  </h3>
                </div>

                {/* Bottom Controls */}
                <div className="flex items-center gap-4 md:gap-6 mt-2">
                  <button
                    onClick={toggleAiMute}
                    className={`p-4 rounded-2xl shadow-xl transition-all hover:scale-110 active:scale-90 border border-white/40 backdrop-blur-xl ${isAiMuted ? 'bg-red-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    title={isAiMuted ? "Unmute AI Voice" : "Mute AI Voice"}
                  >
                    {isAiMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                  </button>

                  <button
                    onClick={() => { if (status === 'idle' || status === 'error') startSession(); }}
                    className={`p-6 md:p-8 rounded-3xl shadow-2xl transition-all transform hover:scale-105 active:scale-95 border-4 ${status === 'listening' ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-white/50 shadow-indigo-500/40 ring-[12px] ring-indigo-500/5' : 'bg-white text-gray-400 border-transparent hover:text-indigo-600'}`}
                  >
                    {status === 'listening' ? <Mic size={32} strokeWidth={2.5} /> : <MicOff size={32} />}
                  </button>

                  <button
                    onClick={resetSession}
                    className="p-4 rounded-2xl bg-white text-gray-600 shadow-xl hover:bg-gray-50 hover:text-indigo-600 transition-all hover:scale-110 active:scale-95 border border-white/40 backdrop-blur-xl"
                    title="Initialize Core"
                  >
                    <RefreshCw size={20} />
                  </button>
                </div>
              </div>
            </div>

            {/* Dynamic Forms Panel - More Compact Glassmorphism */}
            {activeForm !== 'none' && (
              <div className="absolute inset-x-0 bottom-0 top-[15%] md:static md:w-1/2 z-30 flex items-center justify-center p-4 sm:p-6 pointer-events-none">
                <div className="pointer-events-auto w-full max-w-xl h-full max-h-[75vh] md:max-h-[85vh] bg-white/20 backdrop-blur-[30px] rounded-3xl shadow-premium-3d border border-white/40 overflow-hidden flex flex-col transform transition-all duration-1000 animate-in slide-in-from-right-12 fade-in">

                  {/* Subtle Scan Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/[0.02] to-transparent pointer-events-none" />

                  <div className="relative h-full flex flex-col">
                    {activeForm === 'course-selection' && (
                      <CourseSelectionForm
                        data={selectionData}
                        onChange={(f, v) => setSelectionData(p => ({ ...p, [f]: v }))}
                        onSubmit={handleManualSubmit}
                        onCancel={closeForm}
                      />
                    )}
                    {activeForm === 'program-list' && (
                      <CourseDisplay
                        items={visualCourses}
                        onCancel={closeForm}
                      />
                    )}
                    {['fee-structure', 'admission-requirements', 'admission-schedule', 'placement-info', 'contact-info', 'program-details'].includes(activeForm) && (
                      <InfoDisplay
                        type={activeForm}
                        data={infoData}
                        onCancel={closeForm}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default memo(VoiceChatbot);